import { useFeatureFlags } from '@chronosphereio/core';
import { CssBaseline, ThemeProvider as MuiThemeProvider, PaletteMode } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { ElementBreakpointsProvider } from './responsive/ElementBreakpointsProvider';
import { ViewportBreakpointsProvider } from './responsive/ViewportBreakpointsProvider';
import { getTheme } from './theme';
import { usePrefersDarkModeMediaQuery } from './useTheme';

export type ThemeMode = PaletteMode;

export interface ThemeProviderProps {
  /**
   * The main content
   */
  children: ReactNode;
  /**
   * Optional id for the application root. Used to apply global styles to the application
   */
  applicationRoot?: string;
  /** Optional property to specify whether the CSS baseline for the theme should be rendered.
   * Helpful to use in the case of nested theming.
   */
  baselineCss?: boolean;

  /**
   * Optional property to specify that the theme is nested within another theme. When true, theme
   * features that are only needed on the top-level theme (e.g. ViewportBreakpointsProvider) will not be
   * included.
   *
   * Theme nesting is used very rarely to handle edge cases that require a different theme. Currently, it is only
   * used for the "admin" variation of the left nav, which is styled using a dark theme regardless of the overall
   * theming state of the application.
   */
  isNestedTheme?: boolean;

  /**
   * Specifies which theme mode to use, otherwise the provider will fall-back to
   * a calculated default mode.
   */
  mode?: ThemeMode;
}

function isValidTheme(theme: string | null): theme is ThemeMode {
  return theme === 'dark' || theme === 'light';
}

const CHRONO_THEME_PREFERENCE_KEY = 'CHRONOSPHERE_THEME';
const CHRONO_THEME_DEFAULT: ThemeMode = 'light';

/**
 * Encapsulates the Chronosphere theme, passing through to the Material UI
 * theme provider. Will try to provide a default mode when one is not provided
 * by the consumer.
 */
export const ThemeProvider = ({
  children,
  applicationRoot,
  mode,
  baselineCss = true,
  isNestedTheme,
}: ThemeProviderProps) => {
  // Get default value in ascending order of importance
  // 3. Default theme
  let defaultMode: ThemeMode = CHRONO_THEME_DEFAULT;

  // 2. Media query - set at the OS level or in the user agent
  const userSpecifiedDarkMode = usePrefersDarkModeMediaQuery();
  defaultMode = userSpecifiedDarkMode ? 'dark' : defaultMode;

  // 1. Set in local storage - managed by ThemeProvider directly
  const val = window.localStorage.getItem(CHRONO_THEME_PREFERENCE_KEY);
  defaultMode = isValidTheme(val) ? val : defaultMode;

  // Use mode set by ThemeProvider consumer or default

  const themeMode = mode ?? defaultMode;

  // Always sync changes to local storage so we can use as default above
  window.localStorage.setItem(CHRONO_THEME_PREFERENCE_KEY, themeMode);

  const { panwRebrandStubYear } = useFeatureFlags();
  const theme = useMemo(
    () => getTheme(themeMode, { panwRebrandStubYear }, applicationRoot),
    [themeMode, applicationRoot, panwRebrandStubYear]
  );

  return (
    <MuiThemeProvider theme={theme}>
      {baselineCss && <CssBaseline />}
      {isNestedTheme ? (
        children
      ) : (
        // The `ElementBreakpointsProvider` automatically provided is intended to be used for the main content on a
        // page (usually handled by the `ProtectedApp` component in `cloud`). Additional use cases (e.g. Drawers) should
        // configure a separate `ElementBreakpointsProvider` to avoid overriding the configuration for the main content
        // on the page.
        <ViewportBreakpointsProvider>
          <ElementBreakpointsProvider>{children}</ElementBreakpointsProvider>
        </ViewportBreakpointsProvider>
      )}
    </MuiThemeProvider>
  );
};
